import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../../config/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { makeStyles } from "@material-ui/core/styles";
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/SpaceDashboard";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { COLORS } from "../UserDashboard/dashboardConfig";

const useStyles = makeStyles((theme) => ({
  "@keyframes floatA": {
    "0%, 100%": { transform: "translate(0, 0) scale(1)" },
    "50%": { transform: "translate(40px, -30px) scale(1.08)" },
  },
  "@keyframes floatB": {
    "0%, 100%": { transform: "translate(0, 0) scale(1)" },
    "50%": { transform: "translate(-50px, 30px) scale(1.12)" },
  },
  "@keyframes dash": {
    to: { strokeDashoffset: -200 },
  },
  "@keyframes pulse": {
    "0%": { transform: "scale(1)", opacity: 0.7 },
    "100%": { transform: "scale(3.2)", opacity: 0 },
  },
  "@keyframes rise": {
    from: { opacity: 0, transform: "translateY(14px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },

  page: {
    minHeight: "100vh",
    display: "grid",
    gridTemplateColumns: "1.15fr 1fr",
    background: "#fff",
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
  },

  // ---------------- Panel marki ----------------
  hero: {
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: theme.spacing(6),
    color: "#fff",
    background:
      "linear-gradient(145deg, #061a3d 0%, #0d47a1 55%, #1976d2 100%)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3),
      minHeight: 220,
    },
  },
  grid: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
    backgroundSize: "44px 44px",
    maskImage: "radial-gradient(ellipse at 40% 45%, #000 20%, transparent 75%)",
    WebkitMaskImage:
      "radial-gradient(ellipse at 40% 45%, #000 20%, transparent 75%)",
  },
  orbA: {
    position: "absolute",
    width: 420,
    height: 420,
    top: -120,
    right: -100,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(100,181,246,0.55), rgba(100,181,246,0) 70%)",
    animation: "$floatA 14s ease-in-out infinite",
  },
  orbB: {
    position: "absolute",
    width: 360,
    height: 360,
    bottom: -140,
    left: -80,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(33,150,243,0.5), rgba(33,150,243,0) 70%)",
    animation: "$floatB 18s ease-in-out infinite",
  },
  routes: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    opacity: 0.9,
    [theme.breakpoints.down("sm")]: { display: "none" },
  },
  routeLine: {
    fill: "none",
    stroke: "rgba(255,255,255,0.55)",
    strokeWidth: 1.6,
    strokeDasharray: "6 10",
    animation: "$dash 6s linear infinite",
  },
  node: { fill: "#fff" },
  nodePulse: {
    fill: "#90caf9",
    transformOrigin: "center",
    transformBox: "fill-box",
    animation: "$pulse 2.6s ease-out infinite",
  },
  brand: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  brandMark: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: "rgba(255,255,255,0.16)",
    border: "1px solid rgba(255,255,255,0.3)",
    backdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  brandText: { fontSize: 20, fontWeight: 800, letterSpacing: 0.3 },
  pitch: {
    position: "relative",
    maxWidth: 520,
    animation: "$rise 0.8s ease-out both",
    [theme.breakpoints.down("sm")]: { display: "none" },
  },
  headline: {
    margin: 0,
    fontSize: 44,
    lineHeight: 1.1,
    fontWeight: 800,
    letterSpacing: -0.5,
  },
  headlineAccent: {
    background: "linear-gradient(90deg, #90caf9, #e3f2fd)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  lead: {
    margin: theme.spacing(2, 0, 4),
    fontSize: 16,
    lineHeight: 1.6,
    color: "rgba(255,255,255,0.78)",
  },
  features: {
    display: "grid",
    gap: theme.spacing(1.5),
  },
  feature: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.75),
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.14)",
    backdropFilter: "blur(6px)",
    fontSize: 14,
    "& svg": { fontSize: 22, color: "#90caf9", flexShrink: 0 },
    "& b": { display: "block", fontSize: 15, marginBottom: 2 },
    "& span": { color: "rgba(255,255,255,0.7)" },
  },
  foot: {
    position: "relative",
    fontSize: 12,
    color: "rgba(255,255,255,0.55)",
    [theme.breakpoints.down("sm")]: { display: "none" },
  },

  // ---------------- Formularz ----------------
  formSide: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4, 3),
    background: "#fafbfd",
  },
  card: {
    width: "100%",
    maxWidth: 380,
    animation: "$rise 0.7s 0.15s ease-out both",
  },
  title: {
    margin: 0,
    fontSize: 30,
    fontWeight: 800,
    color: "#13294b",
    letterSpacing: -0.3,
  },
  subtitle: {
    margin: theme.spacing(1, 0, 4),
    fontSize: 14.5,
    color: COLORS.textMuted,
  },
  form: { display: "flex", flexDirection: "column", gap: theme.spacing(2.25) },
  field: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      background: "#fff",
      "& svg": { color: "#9e9e9e" },
      "&.Mui-focused svg": { color: COLORS.primary },
    },
  },
  error: { borderRadius: 12, fontSize: 13 },
  submit: {
    height: 48,
    marginTop: theme.spacing(0.5),
    borderRadius: 12,
    textTransform: "none",
    fontSize: 15.5,
    fontWeight: 700,
    color: "#fff",
    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`,
    boxShadow: "0 8px 20px rgba(25, 118, 210, 0.3)",
    transition: "transform 0.15s, box-shadow 0.15s",
    "&:hover": {
      background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`,
      transform: "translateY(-1px)",
      boxShadow: "0 12px 24px rgba(25, 118, 210, 0.38)",
    },
    "&.Mui-disabled": { color: "#fff", opacity: 0.75 },
  },
  "@media (prefers-reduced-motion: reduce)": {
    orbA: { animation: "none" },
    orbB: { animation: "none" },
    routeLine: { animation: "none" },
    nodePulse: { animation: "none" },
    pitch: { animation: "none" },
    card: { animation: "none" },
  },
}));

const warehouseUsers = [
  "dwieteska",
  "aforys",
  "eleszek",
  "lfijolek",
  "pgalinski",
  "mkornata",
  "pbuchowicz",
  "ljuszczynski",
];

// Węzły trasy: [x, y] w układzie viewBox 800x900.
const ROUTE_NODES = [
  [120, 640],
  [330, 520],
  [520, 560],
  [690, 380],
  [560, 230],
];

const Login = ({ setAuthenticated }) => {
  const classes = useStyles();
  const [data, setData] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [isLoginSuccess, setLoginSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.interceptors.request.eject();

    return () => {
      axios.interceptors.request.eject();
    };
  }, []);

  const handleChange = ({ currentTarget: input }) => {
    setData({ ...data, [input.name]: input.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      localStorage.removeItem("token");
      localStorage.removeItem("useNewDb");

      const headers = { "Content-Type": "application/json" };
      const url = `${API_BASE_URL}/login`;

      // Wysyłamy pierwsze zapytanie
      const { data: res } = await axios.post(url, data, { headers });
      localStorage.setItem("token", res.token);

      // Wysyłamy drugie zapytanie po otrzymaniu odpowiedzi z pierwszego
      const { data: userData } = await axios.get(`${API_BASE_URL}/me`, {
        headers: { Authorization: `Bearer ${res.token}` },
      });

      localStorage.setItem("useNewDb", userData.useNewDb ? "true" : "false");

      const authHeaders = {
        Authorization: `Bearer ${res.token}`,
        "X-Use-New-Db": userData.useNewDb ? "true" : "false",
      };
      axios.defaults.headers.common = authHeaders;

      setTimeout(() => {
        setAuthenticated(true);
        setLoginSuccess(true);
      }, 500);
    } catch (error) {
      setLoading(false);
      if (
        error.response &&
        error.response.status >= 400 &&
        error.response.status <= 500
      ) {
        setError(error.response.data.message);
      } else {
        setError("Brak połączenia z serwerem. Spróbuj ponownie.");
      }
    }
  };

  useEffect(() => {
    if (isLoginSuccess) {
      toast.success("Login successful!", {
        autoClose: 1000,
        onClose: () => {
          // Check if the logged-in user should be redirected to warehouse
          if (warehouseUsers.includes(data.username)) {
            navigate("/magazynWarszawaNowyHandling");
          } else {
            navigate("/user");
          }
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoginSuccess]);

  const routePath = ROUTE_NODES.map(
    ([x, y], i) => `${i === 0 ? "M" : "L"}${x} ${y}`,
  ).join(" ");

  return (
    <>
    <div className={classes.page}>
      <section className={classes.hero}>
        <div className={classes.grid} />
        <div className={classes.orbA} />
        <div className={classes.orbB} />
        <svg
          className={classes.routes}
          viewBox="0 0 800 900"
          preserveAspectRatio="xMidYMid slice"
          aria-hidden="true"
        >
          <path className={classes.routeLine} d={routePath} />
          <path
            className={classes.routeLine}
            d="M330 520 L300 330 L430 200"
            style={{ animationDuration: "9s" }}
          />
          <path
            className={classes.routeLine}
            d="M520 560 L650 700 L740 790"
            style={{ animationDuration: "11s" }}
          />
          {ROUTE_NODES.map(([x, y], i) => (
            <g key={i}>
              <circle
                className={classes.nodePulse}
                cx={x}
                cy={y}
                r="6"
                style={{ animationDelay: `${i * 0.5}s` }}
              />
              <circle className={classes.node} cx={x} cy={y} r="5" />
            </g>
          ))}
        </svg>

        <div className={classes.brand}>
          <div className={classes.brandMark}>
            <DashboardIcon />
          </div>
          <span className={classes.brandText}>DutyStation</span>
        </div>

        <div className={classes.pitch}>
          <h1 className={classes.headline}>
            Odprawy i czynności magazynowe
            <br />
            <span className={classes.headlineAccent}>pod kontrolą.</span>
          </h1>
        </div>

        <div className={classes.foot}>
          © {new Date().getFullYear()} DutyStation
        </div>
      </section>

      <section className={classes.formSide}>
        <div className={classes.card}>
          <h2 className={classes.title}>Witaj ponownie 👋</h2>
          <p className={classes.subtitle}>
            Zaloguj się, aby przejść do panelu odpraw.
          </p>
          <form className={classes.form} onSubmit={handleSubmit}>
            <TextField
              className={classes.field}
              label="Nazwa użytkownika"
              name="username"
              value={data.username}
              onChange={handleChange}
              required
              autoFocus
              fullWidth
              autoComplete="username"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              className={classes.field}
              label="Hasło"
              name="password"
              type={showPassword ? "text" : "password"}
              value={data.password}
              onChange={handleChange}
              required
              fullWidth
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label={showPassword ? "Ukryj hasło" : "Pokaż hasło"}
                      onClick={() => setShowPassword((v) => !v)}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {error && (
              <Alert severity="error" className={classes.error}>
                {error}
              </Alert>
            )}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disableElevation
              disabled={loading}
              className={classes.submit}
            >
              {loading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Zaloguj się"
              )}
            </Button>
          </form>
        </div>
      </section>
    </div>
      {/* Poza animowanymi kontenerami - transform rodzica psuje position: fixed toasta */}
      <ToastContainer />
    </>
  );
};

export default Login;
